<template>
  <div
    class="home-wrapper"
    ref="home-wrapper"
    :class="{
      'full-content': isFullContent,
    }"
  >
    <Header
      class="h-14 md:h-15 shadow-top nav-wrapper z-5"
      :class="{
        'full-content': isFullContent,
      }"
    >
      <MainHeader
        :class="{'p-4': isFullContent }"
        :isTemplateSearchVisible="isTemplateSearchVisible"
      />
    </Header>
    <div
      class="main-content"
      :class="{'is-onboarding': hasOnboardingV3, 'is-loading-content': isInlineLoaderVisible}"
    >
      <InlineLoader class="inline-loader__wrapper" v-if="isInlineLoaderVisible" />

      <SideBar
        class="home-side-bar"
        :isSideBarVisible="isSideBarVisible"
      />

      <div class="backdrop" :class="{show: isSideBarBackDropVisible}" @click="hideMenuMobile" />
      <div
        class="router-view-content"
        :class="{
          'full-content': isFullContent,
          'ml-16': hasOnboardingV3,
          'hide': !isRouterViewContentShowing,
          'ml-0': isInlineLoaderVisible,
          'eu': isEURegion
        }"
      >
        <div class="wrapper-participant-limit" v-if="!isInlineLoaderVisible">
          <WarningParticipantLimit :is-editor="false" />
        </div>
        <router-view />
      </div>
      <NotificationPanel v-if="currentUser.plan !== 'white-label' && !isEURegion" />
    </div>

    <Onboarding
      v-if="hasOnboardingV3"
      :hasOnboardingV3="hasOnboardingV3"
      :isOnboardingV3Showing="isOnboardingV3Showing"
    />
    <modal-import
      v-aha-emit-action.view
      v-if="$store.state.openImportModal"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import ImportPPTMixin from '@/mixins/presentation/ImportPPTMixin'
import WarningParticipantLimit from '@/components/UI/WarningParticipantLimit.vue'
import MainHeader from '@/components-v2/common/main-header/MainHeader.vue'
import SideBar from '@/components-v2/common/sidebar/SideBar.vue'
import ModalImport from '@/components-v2/modals/Import.vue'
import InlineLoader from '@/components-v2/common/loading/InlineLoader.vue'
import Onboarding from '@/components-v2/onboarding/Onboarding.vue'

import Config from '@/config'
import { DISALLOWED_ROUTES_FOR_SKOLETUBE } from '@/constant/routes'
import Utils from '@/util'
import Socket from '@/socket'
import PresentationApi from '@/api/presentation'
import constants from '@/constant'

// modules
import offlineWatcher from '@/modules/offlineWatcher'
import * as localStorageExpiry from '@/utils/local.storage.expiry'
import NotificationPanel from '@/components-v2/common/notification/NotificationPanel.vue'

export default {
  components: {
    MainHeader,
    SideBar,
    ModalImport,
    InlineLoader,
    NotificationPanel,
    WarningParticipantLimit,
    Onboarding,
  },
  mixins: [ImportPPTMixin],
  data() {
    return {
      isInlineLoaderVisible: true,
      windowInnerWidth: window.innerWidth,
    }
  },
  computed: {
    ...mapGetters('zoomApp', [
      'isZoomReady',
    ]),
    ...mapState({
      currentUser: (state) => state.currentUser,
      presentations: (state) => state.presentations,
      hasOnboardingV3: (state) => state.indicators.hasOnboardingV3,
      isOnboardingV3Showing: (state) => state.indicators.isOnboardingV3Showing,
      isRouterViewContentShowing: (state) => state.indicators.isRouterViewContentShowing,
      isHomeSideBarShowing: (state) => state.indicators.isHomeSideBarShowing,
    }),
    ...mapGetters(['getActiveSurveys', 'isPaymentPage']),
    isUpgradePage() {
      return this.isPaymentPage
    },
    isTemplateLibraryPage() {
      return this.$route.name === 'TemplateLibrary'
    },
    isFullContent() {
      return this.isUpgradePage
    },
    isTemplateSearchVisible() {
      if (this.$store.state.workingMode === constants.workspace.TEAM) return false
      if (this.$route.name === 'TemplateLibrary') return false
      return (!this.hasOnboardingV3 && this.isRouterViewContentShowing) ||
        (this.hasOnboardingV3 && !this.isOnboardingV3Showing &&
          this.isRouterViewContentShowing && this.isTemplateLibraryPage)
    },
    isOnboardingV3UserGroup() {
      const { createdAt: userCreatedAt } = this.currentUser
      const timeDeployUpdate = 1660896900000 // 19/08/2022 15:15
      return timeDeployUpdate < new Date(userCreatedAt).getTime()
    },
    isOnboardingV3GroupHasPresentation() {
      return this.isOnboardingV3UserGroup &&
        this.currentUser.personalInformation &&
        this.currentUser.personalInformation.hasCreatedPresentation
    },
    isSideBarBackDropVisible() {
      return this.windowInnerWidth < 1024 && this.isHomeSideBarShowing
    },
    isSideBarVisible() {
      return !this.hasOnboardingV3 && this.windowInnerWidth >= 1024
    },
    isWorkspaceBarVisible() {
      if (this.$route.name === 'Upgrade') return false
      return !this.hasOnboardingV3 && this.windowInnerWidth > 1024
    },
    isEURegion() {
      return Config.region === constants.euRegion
    },
  },

  async mounted() {
    document.body.classList.add('pace-done')
    // make sure all the offline requests are sent to the API
    await offlineWatcher.runTasks()
    const currentUser = await this.getCurrentUserAction()
    this._handleSetKeyWhatsNew(currentUser.id)

    const {
      id: userId, identifyProvider,
    } = currentUser

    this.handleImporting(userId)
    this.setFlagUserUpdatedGA4(currentUser)

    if (identifyProvider === 'skoletube') {
      const { name } = this.$router.currentRoute

      if (DISALLOWED_ROUTES_FOR_SKOLETUBE.includes(name)) {
        this.$router.replace({ name: 'Presentations' })
      }
    }

    this.domain = Config.domain
    if (this.isFullContent) this.isInlineLoaderVisible = false
    if (!this.isOnboardingV3UserGroup || this.isOnboardingV3GroupHasPresentation) {
      this.isInlineLoaderVisible = false
      this.$store.state.indicators.isRouterViewContentShowing = true
    }
    this._setOnboardingVisible(currentUser)
    this.listenWindowResize()
    this.getMonthlyToYearlyUpgradePreviewAction()
  },
  destroyed() {
    document.body.classList.remove('pace-done')
    Socket.unSubscribe(`user-${this.$store.state.currentUser.id}`)
    this.unlistenWindowResize()
  },
  methods: {
    ...mapActions([
      'getCurrentUserAction',
      'setHasOnboardingV3Action',
      'setIsOnboardingV3ShowingAction',
      'getMonthlyToYearlyUpgradePreviewAction',
    ]),

    setFlagUserUpdatedGA4(user) {
      if (!user || user.isGuest) {
        return
      }
      const keyFlag = `updated-ga4-${user.id}`
      const updatedGA4 = localStorageExpiry.getWithExpiry(keyFlag)
      if (!updatedGA4) {
        Utils.ga4Config({
          user_id: user.id,
        })
        Utils.ga4Set('user_properties', {
          first_page: user.firstPage,
          referral_user_id: user.referralUserId,
          sign_up_source: user.source,
          use_ahaslides_for: user.personalInformation ? user.personalInformation.useAhaSlidesFor : null,
          updated_at: user.updatedAt,
        })
        localStorageExpiry.setWithExpiry(keyFlag, true, 30 * 24 * 60 * 60 * 1000)
      }

      const keyFlagUpdateSource = `updated-ga4-user-source-${user.id}`
      const updatedUserSourceGA4 = localStorageExpiry.getWithExpiry(keyFlagUpdateSource)
      if (!updatedUserSourceGA4) {
        Utils.ga4Set('user_properties', {
          sign_up_source: user.source,
        })
        localStorageExpiry.setWithExpiry(keyFlagUpdateSource, true, 3650 * 24 * 60 * 60 * 1000)
      }
    },

    logout() {
      Utils.logout(this.currentUser)
      Utils.mixpanelTracking('Presentation List', 'Logout')
    },

    toggleDropdownProfile() {
      this.dropdownProfile = !this.dropdownProfile
      Utils.mixpanelTracking(
        'Presentation List',
        'Profile Menu',
        'Open Profile Menu',
      )
    },

    toggleMiniNavbar() {
      const miniNavbar = 'mini-navbar'

      if (document.body.classList.contains(miniNavbar)) {
        document.body.classList.remove(miniNavbar)
      } else {
        document.body.classList.add(miniNavbar)
      }
    },

    hideMenuMobile() {
      if (window.innerWidth > 1023) return
      this.$store.state.indicators.isHomeSideBarShowing = false
    },

    async _setOnboardingVisible(currentUser) {
      const { admin: isAdmin } = this.$route.query
      const {
        id: userId, identifyProvider, plan,
      } = currentUser
      const personalInformation = currentUser.personalInformation || {}

      if (
        userId
      && !isAdmin
      && plan !== 'white-label'
      && identifyProvider !== 'skoletube'
      ) {
        if (this.isOnboardingV3UserGroup &&
          !personalInformation.hasCreatedPresentation) {
          await this._handleSetShowOnboardingV3(userId)
          return
        }
      } else {
        this.isInlineLoaderVisible = false
        this.$store.state.indicators.isRouterViewContentShowing = true
        return
      }
      this.isInlineLoaderVisible = false
      if (this.hasOnboardingV3) return
      if (this.isOnboardingV3UserGroup) {
        setTimeout(() => {
          this._setShowSideBar()
        }, 500)
      }
    },

    async _handleSetShowOnboardingV3() {
      this.setHasOnboardingV3Action(true)
      if (this.isFullContent) {
        this.$store.state.indicators.isRouterViewContentShowing = true
        return
      }
      const { numberOfPresentations: numberOfSharedPresentations } =
      await PresentationApi.getSharedPresentations()
      const { numberOfPresentations } = await PresentationApi.getPresentations({ limit: 1 })
      this.isInlineLoaderVisible = false

      const shouldShowOnboarding = numberOfSharedPresentations === 0 && numberOfPresentations === 0

      if (shouldShowOnboarding) {
        if (this.$store.state.currentAccount.id && this.$route.name === 'Team') {
          this.setIsOnboardingV3ShowingAction(false)
        } else {
          this.setIsOnboardingV3ShowingAction(true)
          setTimeout(() => {
            this.$store.state.indicators.isRouterViewContentShowing = true
          }, 700)
        }
      } else {
        this.setHasOnboardingV3Action(false)
        this.$store.state.indicators.isRouterViewContentShowing = true
      }
    },

    _handleSetKeyWhatsNew(userId) {
      const keyCheckClick = `${userId}-whats-new-check`
      const isDayShowNoti = this.$func.displayer({ showingOnDays: [4] })
      const today = moment().format('YYYY MM DD')
      const valueKeyWhatsNew = localStorage.getItem(keyCheckClick)

      if (isDayShowNoti && today !== valueKeyWhatsNew) {
        localStorage.removeItem(keyCheckClick)
      }

      if ((isDayShowNoti && today !== valueKeyWhatsNew) || (!isDayShowNoti && !valueKeyWhatsNew)) {
        this.$store.state.hasNews = true
      } else {
        this.$store.state.hasNews = false
      }
    },

    _setShowSideBar() {
      if (window.innerWidth > 1023 && !this.isFullContent) {
        this.$store.state.indicators.isHomeSideBarShowing = true
      }
    },

    listenWindowResize() {
      window.addEventListener('resize', this.onResize)
    },

    unlistenWindowResize() {
      window.removeEventListener('resize', this.onResize)
    },

    onResize() {
      this.windowInnerWidth = window.innerWidth
    },
  },
}
</script>

<style lang="scss" scoped>
.home-wrapper {
  max-height: -webkit-fill-available; // fix height in ios safari (with address bar)
  @apply flex flex-col h-screen;
  &.full-content {
    .main-content {
      @apply pt-0;
    }
  }

  .main-content {
    @apply flex flex-grow bg-primary-50 pt-15;
    &.is-onboarding::v-deep {
      .aha-invitation-notification, .aha-public-announcement {
        z-index: 4 !important;
        @apply lg:-left-18 -left-8;
        width: 102vw;
      }
      .aha-invitation-notification .global-notification-bar {
        display: flex;
        width: 100%;
        justify-content: center;
        padding-left: 1.75rem;
      }
    }
    &.is-loading-content {
      @apply overflow-hidden;
    }
  }
}

.nav-wrapper {
  @apply fixed w-full top-0;
  z-index: 20;
  &.full-content {
    position: static;
  }
}

.backdrop {
  height: 100%;
  width: 100%;
  background-color: #00000060;
  position: fixed;
  z-index: 21;
  visibility: hidden;
  top: 0;
  opacity: 0;
  transition: .3s ease;
}

.backdrop.show {
  opacity: 1;
  visibility: visible !important;
}

.router-view-content {
  @apply bg-primary-50 h-auto flex-grow w-0;
  @media screen and (min-width: 1024px){
    margin-left: 14rem;
  }
  &.ml-16 {
    @apply lg:ml-16 ml-6;
  }
  &.ml-0 {
    margin-left: 0 !important;
  }
  &.full-content {
    @apply bg-base-0 lg:ml-0;
  }
  &.early-bird-registration {
    @apply bg-primary-50;
  }
  &.hide {
    @apply opacity-0;
  }
  &.eu {
    @media screen and (min-width: 1024px){
      margin-left: 14rem;
    }
  }
}

.inline-loader__wrapper {
  @apply h-full w-full flex items-center justify-center overflow-hidden;
}

.wrapper-participant-limit{
  position: sticky;
  top:60px;
  z-index: 21;
}
</style>
